import { useCallback, useEffect, useState } from "react";
import type { Paginated } from "@gatiq/shared";
import { Eye } from "lucide-react";
import {
  Badge,
  Card,
  EmptyState,
  Page,
  PageHeader,
  Pagination,
  SelectField,
  SlideOver,
  Table,
  TD,
  TH,
  THead,
  RowActions,
  TR,
  TableSkeleton,
  useToast,
} from "@/ui";
import { api, ApiClientError } from "@/lib/api";

/** Mirrors `AuditLogDto` from `apps/api/src/services/organizations.ts`. */
interface AuditRow {
  id: string;
  action: string;
  entityType: string | null;
  entityId: string | null;
  actorId: string | null;
  actorName: string | null;
  actorRole: string | null;
  impersonatedBy: string | null;
  orgId: string | null;
  meta: Record<string, unknown> | null;
  ip: string | null;
  createdAt: string;
}

/** Every action the platform can log — kept here rather than in `@gatiq/shared`
 * since it is presentation-only (a filter's option list). */
const ACTIONS = [
  "org_create",
  "org_update",
  "org_suspend",
  "org_activate",
  "org_delete",
  "platform_branding_update",
  "org_branding_update",
  "org_branding_reset",
  "org_settings_update",
  "user_create",
  "user_update",
  "user_delete",
  "user_suspend",
  "karigar_transfer",
  "user_role_change",
  "kyc_submit",
  "kyc_approve",
  "kyc_reject",
  "login_success",
  "login_failed",
  "password_changed",
  "impersonation_start",
  "impersonation_end",
] as const;

function actionLabel(action: string): string {
  return action
    .split("_")
    .map((w) => (w.length ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ");
}

import { ListFilters, useListFilters } from "@/components/ListFilters";
const PAGE_SIZE = 25;

export function PlatformAuditPage() {
  const toast = useToast();
  const [page, setPage] = useState(1);
  const filters = useListFilters(() => setPage(1));
  const [action, setAction] = useState("");
  const [data, setData] = useState<Paginated<AuditRow> | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<AuditRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      filters.apply(qs);
      if (action) qs.set("action", action);
      const res = await api.get<Paginated<AuditRow>>(`/platform/audit?${qs.toString()}`);
      setData(res);
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : "Could not load the audit log.");
    } finally {
      setLoading(false);
    }
  }, [page, action, filters.apply, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [action]);

  return (
    <Page bleed>
      <PageHeader title="Audit log" />

      <Card fill>
        <div className="flex shrink-0 flex-wrap items-end gap-3 border-b border-rule p-4">
          <div className="w-64">
            <SelectField
              label="Action"
              onChange={(e) => setAction(e.target.value)}
              options={ACTIONS.map((a) => ({ value: a, label: actionLabel(a) }))}
              placeholder="All actions"
              value={action}
            />
          </div>
          <ListFilters definitions={data?.filters} filters={filters} />
        </div>

        {loading ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TableSkeleton cols={5} rows={8} />
          </div>
        ) : !data || data.items.length === 0 ? (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <EmptyState message="Nothing has happened yet." title="No audit entries" />
          </div>
        ) : (
          <>
            <Table fill filterable={false}>
              <THead>
                <TR>
                  <TH>Time</TH>
                  <TH>Action</TH>
                  <TH>Actor</TH>
                  <TH>Entity</TH>
                  <TH align="right" width="4.5rem">
                    <span className="sr-only">Actions</span>
                  </TH>
                </TR>
              </THead>
              <tbody>
                {data.items.map((row) => (
                  <TR key={row.id}>
                    <TD className="whitespace-nowrap text-xs text-muted">
                      {new Date(row.createdAt).toLocaleString()}
                    </TD>
                    <TD>
                      <Badge tone="brand">{actionLabel(row.action)}</Badge>
                    </TD>
                    <TD>
                      {row.actorName ?? "—"}
                      {row.impersonatedBy ? (
                        <span className="ml-1.5 text-xs text-warning">(impersonated)</span>
                      ) : null}
                    </TD>
                    <TD className="text-xs text-muted">
                      {row.entityType ?? "—"}
                      {row.entityId ? ` · ${row.entityId.slice(-6)}` : ""}
                    </TD>
                    <TD align="right">
                      <RowActions
                        items={[{ label: "View details", icon: Eye, onSelect: () => setSelected(row) }]}
                        label={actionLabel(row.action)}
                      />
                    </TD>
                  </TR>
                ))}
              </tbody>
            </Table>
            <div className="shrink-0">
              <Pagination
                onChange={setPage}
                page={data.page}
                total={data.total}
                totalPages={data.totalPages}
              />
            </div>
          </>
        )}
      </Card>

      <SlideOver onClose={() => setSelected(null)} open={selected !== null} title="Audit entry">
        {selected ? (
          <div className="flex flex-col gap-4 text-sm">
            <Row label="Action" value={actionLabel(selected.action)} />
            <Row label="When" value={new Date(selected.createdAt).toLocaleString()} />
            <Row label="Actor" value={selected.actorName ?? "—"} />
            <Row label="Actor role" value={selected.actorRole ?? "—"} />
            <Row label="Impersonated by" value={selected.impersonatedBy ?? "—"} />
            <Row label="Organization id" value={selected.orgId ?? "—"} />
            <Row
              label="Entity"
              value={`${selected.entityType ?? "—"}${selected.entityId ? ` · ${selected.entityId}` : ""}`}
            />
            <Row label="IP address" value={selected.ip ?? "—"} />
            <div>
              <p className="mb-1 text-xs font-semibold uppercase tracking-[0.08em] text-muted">Meta</p>
              <pre className="overflow-x-auto rounded-md border border-rule bg-surface p-3 text-xs">
                {selected.meta ? JSON.stringify(selected.meta, null, 2) : "—"}
              </pre>
            </div>
          </div>
        ) : null}
      </SlideOver>
    </Page>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted">{label}</p>
      <p className="mt-0.5 break-words text-ink">{value}</p>
    </div>
  );
}
